import React, { useState, useEffect, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import "./Enquiryform.css";

const Enquiryform = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    dates: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [error, setError] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState("success");
  const formRef = useRef();

  useEffect(() => {
    console.log("Formspree endpoint: xeepbvek");
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    setError(null);
  };

  const closeAlert = () => {
    setShowAlert(false);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    if (!captchaToken) {
      setError("Please verify you're not a robot.");
      setIsSubmitting(false);
      return;
    }

    const dataToSend = {
      name: formData.name,
      email: formData.email,
      mobile: formData.mobile,
      dates: formData.dates,
      message: formData.message,
      "g-recaptcha-response": captchaToken,
      _subject: "New Enquiry Form Submission",
      _replyto: formData.email,
    };

    try {
      const response = await fetch("https://formspree.io/f/xeepbvek", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(dataToSend),
      });

      if (response.ok) {
        setFormData({
          name: "",
          email: "",
          mobile: "",
          dates: "",
          message: "",
        });
        setCaptchaToken(null);
        
        if (window.grecaptcha) {
          window.grecaptcha.reset();
        }
        
        setAlertType("success");
        setShowAlert(true);
        
        setTimeout(() => {
          setShowAlert(false);
        }, 5000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Formspree submission failed.");
      }
    } catch (err) {
      console.error("Form submission error:", err);
      setAlertType("error");
      setShowAlert(true);
      setError(err.message || "An error occurred. Please try again.");
      
      setTimeout(() => {
        setShowAlert(false);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cis-enquiry">
      {/* Custom Alert Modal */}
      {showAlert && (
        <div className="cis-enquiry__alert-overlay" onClick={closeAlert}>
          <div className={`cis-enquiry__alert cis-enquiry__alert--${alertType}`} onClick={(e) => e.stopPropagation()}>
            <div className="cis-enquiry__alert-icon">
              {alertType === "success" ? (
                <svg className="cis-enquiry__alert-checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                  <circle className="cis-enquiry__alert-circle" cx="26" cy="26" r="25" fill="none" />
                  <path className="cis-enquiry__alert-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                </svg>
              ) : (
                <svg className="cis-enquiry__alert-error" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                  <circle className="cis-enquiry__alert-circle-error" cx="26" cy="26" r="25" fill="none" />
                  <path className="cis-enquiry__alert-cross" fill="none" d="M18 18 L34 34 M34 18 L18 34" />
                </svg>
              )}
            </div>
            <h3 className="cis-enquiry__alert-title">
              {alertType === "success" ? "Enquiry Sent Successfully!" : "Submission Failed"}
            </h3>
            <p className="cis-enquiry__alert-message">
              {alertType === "success" 
                ? "Thank you for your enquiry! "
                : error || "There was an error submitting your enquiry. Please try again."
              }
            </p>
            <button className="cis-enquiry__alert-button" onClick={closeAlert}>
              {alertType === "success" ? "Got it, thanks!" : "Try Again"}
            </button>
          </div>
        </div>
      )}

      <div className="cis-enquiry__header">
        <h2 className="cis-enquiry__title">Send Us Your Enquiry</h2>
        <p className="cis-enquiry__subtitle">
          Have questions about our fatherhood programs? We're here to help.
        </p>
      </div>

      <form 
        className="cis-enquiry__form" 
        onSubmit={handleFormSubmit} 
        ref={formRef}
      >
        {/* Honeypot field for spam protection */}
        <input type="text" name="_gotcha" style={{ display: "none" }} tabIndex="-1" autoComplete="off" />
        
        <input type="hidden" name="_subject" value="New Enquiry Form Submission" />
        <input type="hidden" name="_replyto" value={formData.email} />
        <input type="hidden" name="_format" value="plain" />

        <div className="cis-enquiry__grid">
          <div className="cis-enquiry__group">
            <label htmlFor="name" className="cis-enquiry__label">
              Name <span className="cis-enquiry__required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              placeholder="Your full name"
              className="cis-enquiry__input"
            />
          </div>

          <div className="cis-enquiry__group">
            <label htmlFor="email" className="cis-enquiry__label">
              Email <span className="cis-enquiry__required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              placeholder="your.email@example.com"
              className="cis-enquiry__input"
            />
          </div>

          <div className="cis-enquiry__group">
            <label htmlFor="mobile" className="cis-enquiry__label">
              Mobile No. <span className="cis-enquiry__required">*</span>
            </label>
            <input
              type="tel"
              id="mobile"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              placeholder="+1 234 567 890"
              className="cis-enquiry__input"
            />
          </div>

          <div className="cis-enquiry__group">
            <label htmlFor="dates" className="cis-enquiry__label">
              Preferred Contact Date
            </label>
            <input
              type="date"
              id="dates"
              name="dates"
              value={formData.dates}
              onChange={handleChange}
              disabled={isSubmitting}
              className="cis-enquiry__input"
            />
          </div>

          <div className="cis-enquiry__group cis-enquiry__group--full">
            <label htmlFor="message" className="cis-enquiry__label">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="4"
              disabled={isSubmitting}
              placeholder="Tell us about your inquiry or any special requirements..."
              className="cis-enquiry__textarea"
            />
          </div>
        </div>

        {/* reCAPTCHA */}
        <div className="cis-enquiry__recaptcha">
          <ReCAPTCHA
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
            onChange={handleCaptchaChange}
            theme="light"
          />
        </div>

        <button
          type="submit"
          className="cis-enquiry__submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="cis-enquiry__spinner"></span>
              Submitting...
            </>
          ) : (
            "Submit Enquiry"
          )}
        </button>
      </form>
    </div>
  );
};

export default Enquiryform;